import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, PhoneCall, BookOpen, MessageSquare, Network, GitBranch, ArrowRight } from 'lucide-react';
import { useSeo } from '../lib/useSeo';

const products = [
  {
    id: 'lead-gen',
    icon: Sparkles,
    title: 'Generowanie leadów',
    short: 'Klienci sami wpadają, zamiast Ty ich szukać',
    tech: 'FB/LinkedIn Ads + landing page + AI scoring leadów + sync do Twojego CRM',
    bullets: [
      'Targetowanie pod realną intencję zakupową, nie demograficzny strzał',
      'Landing page z quizem kwalifikacyjnym (filtruje "tylko ciekawskich")',
      'Lead scoring AI - Twoja sprzedaż dostaje tylko leady warte rozmowy',
    ],
  },
  {
    id: 'voice-agent',
    icon: PhoneCall,
    title: 'AI Voice Agent 24/7',
    short: 'Telefon zawsze odebrany, spotkanie zawsze umówione',
    tech: 'Retell.ai + ElevenLabs (głos PL) + integracja z Twoim kalendarzem i CRM',
    bullets: [
      'Odbiera 99%+ połączeń, też w nocy i w weekendy',
      'Sam umawia spotkania w Twoim Google Calendar / Calendly',
      'Pyta o budżet, branżę, problem - przekazuje gotowy brief',
    ],
  },
  {
    id: 'rag',
    icon: BookOpen,
    title: 'Inteligentna baza wiedzy',
    short: 'Klient pyta, AI odpowiada - z Twojej dokumentacji',
    tech: 'RAG (pgvector + Claude) - system uczy się Twoich produktów, FAQ, polityk',
    bullets: [
      'Odpowiada na 80-95% ticketów support automatycznie',
      'Zna Twoją instrukcję obsługi, regulamin zwrotów, specyfikacje produktów',
      'Eskaluje do człowieka tylko nietypowe sprawy - reszta załatwiona w 4 sekundy',
    ],
  },
  {
    id: 'sms',
    icon: MessageSquare,
    title: 'SMS Reaktywacja',
    short: 'Stare leady i klienci wracają sami',
    tech: 'Twilio + AI segmentacja bazy + osobiste wiadomości pisane pod każdego',
    bullets: [
      'Reaktywuje 15-30% bazy która "spała" przez miesiące',
      'Każda wiadomość pisana indywidualnie (nie spam-szablon)',
      'Działa też dla porzuconych koszyków e-commerce',
    ],
  },
  {
    id: 'aios',
    icon: Network,
    title: 'AIOS - Twój własny zespół AI',
    short: 'Jak ChatGPT Team, ale specjalizowani pod Twoją firmę',
    tech: 'Multi-agent system w Discord - każdy agent ma jedno zadanie i robi je 24/7',
    bullets: [
      'Agent obsługi klienta zna Twoje FAQ',
      'Agent sprzedażowy zna Twoich klientów',
      'Agent contentowy pisze w Twoim tonie głosu',
      'Agent operacyjny ogarnia rutynę raportowania',
    ],
  },
  {
    id: 'workflow',
    icon: GitBranch,
    title: 'Automatyzacja workflow n8n',
    short: 'Łączymy systemy które do tej pory nie gadały',
    tech: 'n8n + Trigger.dev + ~200 integracji API (Pipedrive, Allegro, BaseLinker, Slack...)',
    bullets: [
      'Lead z LinkedIn → automatycznie w Pipedrive z otagowaniem',
      'Codzienny raport reklam → Slack, bez Twojej ingerencji',
      'Przelew z Allegro → księgowość → faktura → mail do klienta - w 30 sekund',
    ],
  },
];

export default function UslugiPage() {
  const navigate = useNavigate();
  useSeo({
    title: 'Usługi - DAIEN | Systemy AI dla firm B2B',
    description: 'Lead generation, voice agenci AI 24/7, customer support RAG i automatyzacja workflow. Zobacz, jakie systemy AI wdrażam dla firm B2B i jak wyglądają efekty.',
    path: '/uslugi',
  });
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-bg text-text-primary">
      <header className="px-6 md:px-10 lg:px-16 pt-10 pb-6 max-w-6xl mx-auto">
        <Link to="/" className="text-sm text-muted hover:text-text-primary transition-colors inline-flex items-center gap-2">
          ← DAIEN
        </Link>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-20 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <div className="inline-flex items-center gap-4 mb-6">
            <span className="w-8 h-px bg-stroke"></span>
            <span className="text-xs text-muted uppercase tracking-[0.3em]">Co konkretnie wdrażam</span>
            <span className="w-8 h-px bg-stroke"></span>
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl tracking-tight font-light mb-6">
            6 produktów <span className="font-display italic">które działają</span>
          </h1>
          <p className="text-muted text-lg max-w-2xl mx-auto">
            Lead gen, voice agents, inteligentne bazy wiedzy, SMS, AIOs, automatyzacja workflow.
            Każdy produkt = realny case w produkcji u klientów. Wdrażam dokładnie ten sam stack którego sam używam.
          </p>
        </motion.div>
      </section>

      {/* 6 produktów grid */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 lg:px-16 pb-16 md:pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {products.map((product, i) => {
            const Icon = product.icon;
            return (
              <motion.article
                key={product.title}
                id={product.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 + i * 0.08, ease: "easeOut" }}
                className="bg-surface border border-stroke rounded-3xl p-8 md:p-10 hover:border-text-primary/30 transition-colors group scroll-mt-24"
              >
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-bg border border-stroke flex items-center justify-center text-accent-to shrink-0">
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-2xl md:text-3xl font-light text-text-primary mb-1">{product.title}</h2>
                    <p className="text-muted text-sm italic">{product.short}</p>
                  </div>
                </div>
                <p className="text-sm text-muted mb-6 pb-6 border-b border-stroke font-mono">
                  {product.tech}
                </p>
                <ul className="space-y-3">
                  {product.bullets.map(bullet => (
                    <li key={bullet} className="flex items-start gap-3 text-sm text-text-primary">
                      <span className="text-accent-from mt-1.5 shrink-0">•</span>
                      <span className="leading-relaxed">{bullet}</span>
                    </li>
                  ))}
                </ul>
              </motion.article>
            );
          })}
        </div>
      </section>

      {/* AIOS gap-selling sekcja */}
      <section className="bg-surface border-y border-stroke py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="flex items-center gap-4 mb-6">
              <span className="w-8 h-px bg-stroke"></span>
              <span className="text-xs text-muted uppercase tracking-[0.3em]">Jeden system zamiast 6 narzędzi</span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-light mb-8 leading-[1.1]">
              AI dopasowane do <span className="font-display italic">twojego biznesu</span> - w AIOS
            </h2>
            <div className="space-y-6 text-lg text-muted leading-relaxed mb-12">
              <p>
                AIOS to system w którym agenci AI uczą się specyfiki Twojej firmy: Twój ton głosu,
                Twój produkt, Twoich klientów. Każdy agent ma jedno zadanie i robi je 24/7.
              </p>
              <ul className="space-y-3 text-text-primary">
                <li className="flex items-start gap-3"><span className="text-accent-from mt-1.5">→</span> Agent obsługi klienta zna Twoje FAQ</li>
                <li className="flex items-start gap-3"><span className="text-accent-from mt-1.5">→</span> Agent sprzedażowy zna Twoich klientów</li>
                <li className="flex items-start gap-3"><span className="text-accent-from mt-1.5">→</span> Agent contentowy pisze w Twoim stylu</li>
                <li className="flex items-start gap-3"><span className="text-accent-from mt-1.5">→</span> Agent operacyjny ogarnia rutynę raportowania</li>
              </ul>
              <p>
                Nie kupujesz "AI" jako abstrakcję. Kupujesz konkretnych agentów, którzy zastępują
                konkretne czynności w Twojej firmie. Sam używam AIOS codziennie do prowadzenia DAIEN
                - to nie jest demo. To produkcja.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => navigate('/quiz')}
                className="inline-flex items-center gap-2 bg-text-primary text-bg px-8 py-4 rounded-full text-base font-medium hover:scale-[1.02] transition-transform"
              >
                Wypełnij quiz kwalifikacyjny
                <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="https://calendar.app.google/4V6jKtrVUtMihmoE9"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 border border-stroke text-text-primary px-8 py-4 rounded-full text-base font-medium hover:bg-bg transition-colors"
              >
                Umów 20-min rozmowę
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer link */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 lg:px-16 py-16 text-center">
        <p className="text-sm text-muted mb-4">Chcesz zobaczyć realne case studies wdrożeń?</p>
        <Link to="/blog" className="inline-flex items-center gap-2 text-text-primary hover:text-accent-to transition-colors">
          → Zobacz blog z case studies
        </Link>
      </section>
    </div>
  );
}
